import {
  Shovel, Droplet, Sprout, Leaf, TreeDeciduous, Wheat,
} from 'lucide-react';

/**
 * personMeta — the shared vocabulary for a prayer recipient.
 *
 * The journey stage and prayer cadence were previously duplicated between
 * RecipientCard, PrayerLayout and PrayerScreen's modal. Now that the list lives
 * in People while the modal stays in Prayer, they need one copy.
 *
 * NOTE the six stages are still a plain string column (`witnessStage`) with no
 * DB constraint. Phase B moves them into data proper; until then this module is
 * the frontend's single source of truth and everything should read from here.
 */

// Six-phase discipleship journey mapped to the Four Chairs framework.
// Till/Soil/Seed are pre-faith (Chair 1: Come & See).
// Sprout/Plant/Harvest are following Jesus (Chairs 2-4).
export const WITNESS_FLOW = ['till', 'soil', 'seed', 'sprout', 'plant', 'harvest'];
export const PRE_FAITH_STAGES  = ['till', 'soil', 'seed'];
export const POST_FAITH_STAGES = ['sprout', 'plant', 'harvest'];

const BELIEVER_SET = new Set(POST_FAITH_STAGES);

export const STAGE_ICONS = {
  till: Shovel, soil: Droplet, seed: Sprout,
  sprout: Leaf, plant: TreeDeciduous, harvest: Wheat,
};

/**
 * Records predating the journey feature — or carrying an old vocabulary value
 * — must still render, so anything unrecognised reads as the first stage
 * rather than blanking the pill.
 */
export function stageOf(recipient) {
  const s = recipient?.witnessStage;
  return WITNESS_FLOW.includes(s) ? s : 'till';
}

export function isBelieverStage(stage) {
  return BELIEVER_SET.has(stage);
}

/**
 * Oikos = people you are reaching who don't yet follow Jesus. Derived purely
 * from the chair; there is no separate hasFaith field. Memorials are excluded.
 */
export function isOikos(recipient) {
  return !!recipient && !recipient.isMemorial && !isBelieverStage(stageOf(recipient));
}

/**
 * Colour per prayer cadence. Memorials override to purple regardless of
 * cadence — the candle, not the schedule, is what matters about them.
 */
const CADENCE_META = {
  normal: { color: '#2A6B6E', bg: '#EAF4F4' },
  high:   { color: '#C07800', bg: '#FDF6E3' },
  urgent: { color: '#C0392B', bg: '#FDF0F0' },
};
const MEMORIAL_META = { color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' };

export function cadenceMeta(recipient) {
  if (recipient?.isMemorial) return MEMORIAL_META;
  return CADENCE_META[recipient?.priority] || CADENCE_META.normal;
}

/**
 * The label under a person's name.
 *
 * The stored values are still urgent|high|normal, but we render them as
 * Daily / Weekly / Rotation. Nobody should see a person ranked "Urgent" next
 * to their face — the field has always meant how often you pray for them, and
 * now it reads that way. See prayer.priorities in the locale files.
 */
export function cadenceLabel(recipient, t) {
  if (recipient?.isMemorial) return t('prayer.memorial.recipientTag');
  return t(`prayer.priorities.${recipient?.priority || 'normal'}`);
}

export function initialsOf(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

/**
 * Where a conversation happened.
 *
 * Lives here rather than in a tab component because three screens name a
 * setting — the History tab, the Encounters list and the planning form — and
 * the first two used to import it from DiscipleMakingTab, which no longer
 * renders a conversation at all.
 *
 * The six presets are translated; anything else is the user's own free text and
 * is shown exactly as they typed it.
 */
export const SETTING_ICON = {
  boat: '🚤', truck: '🛻', coffee: '☕', jobsite: '🔨', meal: '🍽️', walk: '🚶',
};
const SETTING_PRESETS = Object.keys(SETTING_ICON);

export function settingLabel(setting, t) {
  if (!setting) return t('encounters.aConversation');
  return SETTING_PRESETS.includes(setting) ? t(`encounters.settings.${setting}`) : setting;
}
